/**************************************** PROJECT MODULES *****************************************/
import {action} from 'mobx';
import {Store, setLoginCookie} from '@canimmunize/core-imports';
import {BroadcastEvent} from '@canimmunize/javelinscript';

/************************************** THIRD-PARTY MODULES ***************************************/
import {AxiosResponse} from 'axios';
import {getLangFromUrlPathname} from 'mad-utils/lib/react';

/********************************************* CONFIG *********************************************/
const okStatusCode = 200;

/******************************************** EXPORTS *********************************************/
/**
 * Build response error string, log it, then throw the error
 *
 * @param {Response} response Fetch response object
 * @param {string} srcFn Name of originating function
 *
 * @return {never} Doesn't return - this method purposely throws an error
 */
const throwResponseError = (res: Response, srcFn: string): never => {
    const errData =
        `${srcFn} -- ${res.type} request to ${res.url} failed. \nResponse has status ` +
        `${res.status}\n...and body: ${res.body} with keys: ${Object.keys(res.body)}`;
    console.error(`${errData} \nResponse object ::`, res);
    throw new Error(errData);
};

/**
 * Get response data based on the accepted method calls
 * A bit dirty, but handles both the text and json data return cases
 *
 * @param {Response} res Unparsed response data stream from a fetch call
 * @param {boolean} emptyOK If true, allows empty responses that parse to neither text or JSON
 * @param {string} srcFn Name of calling function, for logging purposes [OPTIONAL]
 *
 * @return {Promise<string|object|never>} Promise wrapped result of handling the response stream
 */
export const getResponseDataSafe = async (
    res: Response,
    emptyOk = true,
    srcFn: string = ''
): Promise<string | never | {}> => {
    try {
        return await res.json();
    } catch (err) {
        try {
            return await res.text();
        } catch (err) {
            if (emptyOk && res.status === okStatusCode) {
                return await '';
            }
            throwResponseError(res, srcFn);
        }
    }
};

/****************** AUTOMATIC EVENT HANDLER CREATION AND BINDING (TO COMPONENTS) ******************/
// TODO try to magically create & bind set_stateProp actions to MobX using this function
// VERY, VERY EVIL
/**
 * Generate event handlers for modifying a component's MobX store
 * Example: addEventHandlers.call(this, ['name', 'email'], 'appState');
 *
 * IMPORTANT: Must be called within component constructor
 *
 * ----------------------------------------------------------------------------
 * Creates new event handlers to modify properties on a component's MobX store,
 * at paths with the naming scheme this.events.${curStateProp}Change
 *
 * Example: if stateProps arg is ['name', 'email'], it will add event handlers
 *          this.events.nameChange & this.events.emailChange to the component
 *
 * Calling added event handlers will mutate the corresponding store props
 * (store.name, store.email), by calling set_{storeProp} on the MobX store
 * (full path from inside the component: this.props.storeProp.set_{storeProp})
 *
 * IMPORTANT: For this to work, you need actions with these names on the MobX
 *            store: 1 for each property in the stateProps array; & the
 *            properties themselves must be added to the store & be observable
 *
 * Example: The store referenced above needs actions set_name & set_email,
 *          along with properties name & email (which must be observable):
 *
 *             class SomeStore {
 *                 @observable name: string;
 *                 @observable email: string;
 *
 *                 constructor() {
 *                     this.name = '';
 *                     this.email = '';
 *                 }
 *
 *                 @action public set_name(val: string) { this.name = val }
 *                 @action public set_email(val: string) { this.email = val }
 *             }
 *
 * ** Passing events down **
 * To pass events to children without typing issues, you may need to get the type
 * of this.events, then assign it to the this.events item passed to the child
 *
 * Example: In render method:
 *     const ev = this.events;
 *     return (<SignupView events={this.events as typeof ev} />)
 *
 * ESSENTIAL:
 *     *** Must be run in constructor, and must be bound to 'this' when called (e.g. via 'call')
 *     *** You must create set_{stateProp} actions on the MobX store for prop mutation to work
 *
 * @param {string[]} stateProps Names of store properties to create modifier methods for
 * @param {string} storeProp Prop the MobX store can be found at
 *
 * @return {Object} events object with newly added event handlers
 * @magic @evil
 */
export const addStoreMutatingEventHandlers = function addMobXEventHandlers(
    storeProp: string,
    stateProps: string[]
) {
    this.state = this.state || {};

    stateProps.forEach(stateProp => {
        const handlerName = `${stateProp}Change`;

        this.events[handlerName] = (ev: React.FormEvent<HTMLInputElement>) => {
            const fullName = `${this.constructor.name}#${handlerName}`;
            const val = typeof ev === 'string' ? ev : ev.target['value'];
            this.props[storeProp][`set_${stateProp}`](val);
        };

        Reflect.defineProperty(this.events[handlerName], 'name', {
            value: handlerName,
        });
    });

    return this.events;
};

/**
 * TODO MOVE PATH TO CONFIG FILE
 * Redirect user to login page
 * Do this non-dynamically (i.e. trigger full page reload)
 * This should always set the user to being logged out, to avoid a redirect loop
 */
export const redirectToLogin = () => {
    setLoginCookie(false);
    (window as any).location = `/auth/${getLangFromUrlPathname()}/login`;
};

export interface AxiosErrorResponse {
    response: AxiosResponse;
    message: string;
}
